import type { GroupBase, StylesConfig } from 'react-select';

import type { TOption } from './types';

/**
 * Shared react-select styling for `Select` and `AsyncSelect`.
 *
 * Extracted verbatim from the original `Select` so both variants stay visually
 * identical — the calculator and the auction filters must not drift apart.
 * Behaviour is unchanged; this is a move, not a redesign.
 */
export const createSelectStyles = ({
  blacked,
  isHovered,
}: {
  blacked: boolean;
  isHovered: boolean;
}): StylesConfig<TOption, false, GroupBase<TOption>> => {
  const controlBorderColor = blacked ? 'rgba(18, 18, 18, 0.75)' : 'rgba(229, 229, 229, 0.28)';
  const controlHoverColor = blacked ? controlBorderColor : 'rgba(251, 192, 11, 0.85)';
  const controlBackground = blacked ? 'transparent' : 'rgba(27, 27, 27, 0.6)';
  const controlShadow = blacked ? 'none' : 'inset 0 1px 1px rgba(255, 255, 255, 0.08)';
  const controlFocusShadow = blacked
    ? '0 0 0 1.5px rgba(18, 18, 18, 0.45)'
    : '0 0 0 1.5px rgba(251, 192, 11, 0.45)';
  const placeholderColor = blacked ? 'rgba(18, 18, 18, 0.6)' : 'rgba(229, 229, 229, 0.5)';
  const textColor = blacked ? 'rgba(18, 18, 18, 0.92)' : 'rgba(229, 229, 229, 0.92)';
  const menuBackground = 'rgba(18, 18, 18, 0.65)';
  const menuBorderColor = 'rgba(255, 255, 255, 0.15)';
  const optionHoverBackground = '#FBC00B';
  const optionHoverColor = '#1A1A1A';

  return {
    control: (baseStyles, state) => ({
      ...baseStyles,
      backgroundColor: controlBackground,
      borderWidth: '1px',
      borderRadius: '0.85rem',
      padding: '0px',
      borderColor: state.isFocused || isHovered ? controlHoverColor : controlBorderColor,
      outline: 'none',
      transition: 'border-color 150ms ease, box-shadow 150ms ease, background-color 150ms ease',
      cursor: 'pointer',
      color: textColor,
      boxShadow: state.isFocused ? controlFocusShadow : controlShadow,
      minHeight: '44px',

      '&:hover': {
        borderColor: controlHoverColor,
      },
    }),
    valueContainer: baseStyles => ({
      ...baseStyles,
      padding: '0 14px',
      gap: '6px',
    }),
    placeholder: baseStyles => ({
      ...baseStyles,
      color: placeholderColor,
      fontWeight: 600,
      letterSpacing: '0.025em',
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
    }),
    input: baseStyles => ({
      ...baseStyles,
      color: textColor,
      fontWeight: 600,
      letterSpacing: '0.025em',
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
    }),
    singleValue: baseStyles => ({
      ...baseStyles,
      paddingLeft: '2px',
      color: textColor,
      fontWeight: 600,
      letterSpacing: '0.025em',
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
    }),
    menu: baseStyles => ({
      ...baseStyles,
      backgroundColor: menuBackground,
      borderRadius: '1rem',
      border: `1px solid ${menuBorderColor}`,
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.55)',
      marginTop: '10px',
      overflow: 'hidden',
      backdropFilter: 'blur(14px)',
    }),
    menuList: baseStyles => ({
      ...baseStyles,
      padding: '4px',
    }),
    option: (baseStyles, { isFocused }) => ({
      ...baseStyles,
      fontWeight: 600,
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      letterSpacing: '0.025em',
      backgroundColor: 'transparent',
      color: textColor,
      padding: '10px 12px',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transitionProperty: 'color, background-color, border-color, text-decoration-color, fill, stroke',
      transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
      transitionDuration: '150ms',

      '&:hover': {
        color: optionHoverColor,
        backgroundColor: optionHoverBackground,
      },

      ...(isFocused && {
        color: optionHoverColor,
        backgroundColor: optionHoverBackground,
      }),
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
  };
};
